import { forwardRef, useCallback, useState } from 'react'
import Image from 'next/image'
import classnames from 'classnames'
import { m, LazyMotion, domAnimation } from 'framer-motion'

import TalentImage from '../../../assets/backgrounds/imgTalent.png'
import CompanyImage from '../../../assets/backgrounds/imgCompanies.png'
import CTAButton from '../../button'

import styles from './index.module.scss'

const ANIMATIONS = {
  TALENT: 'TALENT',
  COMPANY: 'COMPANY',
}

// Animations
const ANIMATION_PROPS = {
  variants: {
    start: { y: '50%', opacity: 0.1 },
    end: {
      y: '0%',
      opacity: 1,
      transition: { type: 'spring', bounce: 0 },
    },
  },
  initial: 'start',
  whileInView: 'end',
  viewport: { once: true },
}

export const AboutUsTheTrustNeeded = forwardRef<HTMLElement>((_, ref) => {
  const [currentAnimation, setCurrentAnimation] = useState<string | undefined>(undefined)

  const onTalentAnimation = useCallback(() => {
    setCurrentAnimation(ANIMATIONS.TALENT)
  }, [])

  const onCompanyAnimation = useCallback(() => {
    setCurrentAnimation(ANIMATIONS.COMPANY)
  }, [])

  const onRemoveAnimation = useCallback(() => {
    setCurrentAnimation(undefined)
  }, [])
  return (
    <section ref={ref} className={styles['wrapper']}>
      <Image
        src={TalentImage}
        className={classnames(styles['background-image'], styles['talent-background-image'], {
          [styles['image-visible']]: currentAnimation === ANIMATIONS.TALENT,
        })}
        alt="Person smiling while working with a team in a desk with personal computers"
        fill
        sizes="100vw"
      />
      <Image
        src={CompanyImage}
        className={classnames(styles['background-image'], styles['companies-background-image'], {
          [styles['image-visible']]: currentAnimation === ANIMATIONS.COMPANY,
        })}
        alt="Person smiling while closing a deal by handshaking another person"
        fill
        sizes="100vw"
      />
      <LazyMotion features={domAnimation} strict>
        <div className={styles['content']}>
          <m.h2 className={styles['title']} {...ANIMATION_PROPS}>
            The trust needed for a{' '}
            <span className={styles['highlight']}>successful connection.</span>
          </m.h2>
          <m.p className={styles['description']} {...ANIMATION_PROPS}>
            We create a universe for talent and companies that seek each other out and find one
            another. We are not a “hire-only“ company, we are on this path together, hand in hand,
            on a continuous road.
          </m.p>
          <m.p className={styles['text']} {...ANIMATION_PROPS}>
            Let's <span className={styles['highlight']}>explore</span> your new journey
          </m.p>
          <CTAButton
            href="/for-talent"
            className={classnames(styles['talent-button'], styles['button'])}
            onMouseEnter={onTalentAnimation}
            onFocus={onTalentAnimation}
            onMouseLeave={onRemoveAnimation}
            onBlur={onRemoveAnimation}
            variant={currentAnimation === ANIMATIONS.COMPANY ? 'opacity' : 'highlight'}
            prefetch={false}
          >
            for talent
          </CTAButton>
          <CTAButton
            href="/for-companies"
            onMouseEnter={onCompanyAnimation}
            onFocus={onCompanyAnimation}
            onMouseLeave={onRemoveAnimation}
            onBlur={onRemoveAnimation}
            variant={currentAnimation === ANIMATIONS.TALENT ? 'opacity' : 'highlight'}
            className={styles['button']}
            prefetch={false}
          >
            for companies
          </CTAButton>
          <CTAButton
            href="/for-talent"
            className={classnames(styles['talent-button'], styles['mobile-button'])}
            variant="highlight"
            size="medium"
            prefetch={false}
          >
            for talent
          </CTAButton>
          <CTAButton
            href="/for-companies"
            variant="highlight"
            className={styles['mobile-button']}
            size="medium"
            prefetch={false}
          >
            for companies
          </CTAButton>
        </div>
      </LazyMotion>
    </section>
  )
})
